/**
 * Category Service
 * Manages product categories with localStorage persistence and IndexedDB image storage
 */

import * as imageStore from './imageStore';

const STORAGE_KEY = 'geppro_categories';

// ============================================================
// Storage Helpers
// ============================================================
function loadCategories() {
  try {
    const json = localStorage.getItem(STORAGE_KEY);
    return json ? JSON.parse(json) : [];
  } catch {
    return [];
  }
}

function saveCategories(categories) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(categories));
  } catch (err) {
    console.error('Failed to save categories:', err);
  }
}

// ============================================================
// In-Memory Database
// ============================================================
const db = {
  categories: loadCategories(),
};

// ============================================================
// Service Methods
// ============================================================
const categoryService = {
  /**
   * Fetch all categories for a specific item type
   */
  async fetchCategories(itemType = 'Mercadoria') {
    return new Promise(resolve => {
      setTimeout(() => {
        const filtered = db.categories.filter(c => c.itemType === itemType);
        resolve(filtered);
      }, 150);
    });
  },

  /**
   * Create a new category
   */
  async createCategory(payload) {
    return new Promise(async resolve => {
      try {
        const { name, description, itemType, imageFile } = payload;

        // Check if category with this name already exists
        const exists = db.categories.some(
          c => c.name.toLowerCase() === name.toLowerCase() && c.itemType === itemType
        );

        if (exists) {
          resolve({ error: 'Já existe uma categoria com esse nome.' });
          return;
        }

        // Save image to IndexedDB if provided
        let imageKey = null;
        if (imageFile instanceof File) {
          imageKey = await imageStore.saveImage(imageFile);
        }

        // Create category
        const newId = db.categories.length > 0
          ? Math.max(...db.categories.map(c => c.id)) + 1
          : 1;

        const newCategory = {
          id: newId,
          name,
          description: description || '',
          itemType,
          imageKey,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };

        db.categories.push(newCategory);
        saveCategories(db.categories);

        resolve({ success: true, category: newCategory });
      } catch (err) {
        console.error('createCategory error:', err);
        resolve({ error: 'Erro ao criar categoria' });
      }
    });
  },

  /**
   * Update an existing category
   */
  async updateCategory(id, payload) {
    return new Promise(async resolve => {
      try {
        const index = db.categories.findIndex(c => c.id === id);
        if (index === -1) {
          resolve({ error: 'Categoria não encontrada' });
          return;
        }

        const { name, description, imageFile } = payload;
        const category = db.categories[index];

        // Check duplicate name (excluding current category)
        const duplicate = db.categories.some(
          c => c.id !== id && 
               c.name.toLowerCase() === name.toLowerCase() && 
               c.itemType === category.itemType
        );

        if (duplicate) {
          resolve({ error: 'Já existe uma categoria com esse nome.' });
          return;
        }

        // Update image if new file provided
        let imageKey = category.imageKey;
        if (imageFile instanceof File) {
          // Delete old image
          if (category.imageKey) {
            try { await imageStore.deleteImage(category.imageKey); } catch {}
          }
          imageKey = await imageStore.saveImage(imageFile);
        }

        // Update category
        db.categories[index] = {
          ...category,
          name,
          description: description || '',
          imageKey,
          updatedAt: new Date().toISOString(),
        };

        saveCategories(db.categories);
        resolve({ success: true, category: db.categories[index] });
      } catch (err) {
        console.error('updateCategory error:', err);
        resolve({ error: 'Erro ao atualizar categoria' });
      }
    });
  },

  /**
   * Delete a category
   */
  async deleteCategory(id) {
    return new Promise(async resolve => {
      try {
        const category = db.categories.find(c => c.id === id);
        if (!category) {
          resolve({ error: 'Categoria não encontrada' });
          return;
        }

        // Delete image from IndexedDB
        if (category.imageKey) {
          try { await imageStore.deleteImage(category.imageKey); } catch {}
        }

        // Remove category
        db.categories = db.categories.filter(c => c.id !== id);
        saveCategories(db.categories);

        resolve({ success: true });
      } catch (err) {
        console.error('deleteCategory error:', err);
        resolve({ error: 'Erro ao excluir categoria' });
      }
    });
  },

  /**
   * Get category image URL from key
   */
  async getCategoryImageUrl(imageKey) {
    if (!imageKey) return null;
    return await imageStore.getImageUrl(imageKey);
  },
};

export default categoryService;
